import { positionIsRoleWord } from '@fieldops/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Avatar, Badge, Button, Field, Input } from '../ui.js'

/** A person on a client or on one of its sites. Decision 85. */
export interface ClientPerson {
  id: string
  firstName: string
  lastName: string | null
  position: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  isPrimary: boolean
  siteId: string | null
}

/**
 * The people at a client, or at one site.
 *
 * Creating and editing happen here, on the client record. The job card can
 * add a new person too, through the same `ContactFields`, and editing one
 * stays here.
 */
export function ContactList({
  clientId,
  siteId,
  people,
  canEdit,
}: {
  clientId: string
  /** Null lists the client's own contacts. A site id lists that property's. */
  siteId: string | null
  people: ClientPerson[]
  canEdit: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<ClientPerson | 'new' | null>(null)

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.clients.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const remove = useMutation(trpc.clients.removeContact.mutationOptions({ onSuccess: invalidate }))
  const makePrimary = useMutation(
    trpc.clients.setPrimaryContact.mutationOptions({ onSuccess: invalidate }),
  )

  return (
    <div>
      {people.length === 0 && draft === null ? (
        siteId ? null : (
          <p className="px-[var(--row-pad-x)] py-3 text-sm text-ink-500">No contacts yet.</p>
        )
      ) : (
        <ul className="divide-y divide-ink-100">
          {people.map((person) =>
            draft && draft !== 'new' && draft.id === person.id ? (
              <li key={person.id} className="p-[var(--row-pad-x)]">
                <ContactForm
                  clientId={clientId}
                  siteId={siteId}
                  person={person}
                  onDone={() => setDraft(null)}
                />
              </li>
            ) : (
              <li key={person.id} className="flex items-start gap-2.5 p-[var(--row-pad-x)]">
                <Avatar name={personName(person)} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink-900">
                    {personName(person)}
                    {person.isPrimary && siteId == null ? (
                      <Badge tone="green" className="ml-2">
                        Primary
                      </Badge>
                    ) : null}
                  </p>
                  <p className="truncate text-xs text-ink-500">{personLine(person)}</p>
                  {canEdit ? (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      <TextButton onClick={() => setDraft(person)}>Edit</TextButton>
                      {siteId == null && !person.isPrimary ? (
                        <TextButton
                          disabled={makePrimary.isPending}
                          onClick={() => makePrimary.mutate({ id: person.id })}
                        >
                          Make primary
                        </TextButton>
                      ) : null}
                      <TextButton
                        tone="danger"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate({ id: person.id })}
                      >
                        Remove
                      </TextButton>
                    </div>
                  ) : null}
                </div>
              </li>
            ),
          )}
        </ul>
      )}

      {canEdit && draft === 'new' ? (
        <div className="border-t border-ink-100 p-[var(--row-pad-x)]">
          <ContactForm clientId={clientId} siteId={siteId} person={null} onDone={() => setDraft(null)} />
        </div>
      ) : null}

      {canEdit && draft === null ? (
        <div className="border-t border-ink-100 px-[var(--row-pad-x)] py-2">
          <Button size="sm" onClick={() => setDraft('new')}>
            Add a contact
          </Button>
        </div>
      ) : null}

      {remove.error ? (
        <p className="px-[var(--row-pad-x)] pb-2 text-xs text-red-600" role="alert">
          {errorMessage(remove.error)}
        </p>
      ) : null}
    </div>
  )
}

function ContactForm({
  clientId,
  siteId,
  person,
  onDone,
}: {
  clientId: string
  siteId: string | null
  person: ClientPerson | null
  onDone: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const save = useMutation(
    trpc.clients.saveContact.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        onDone()
      },
    }),
  )

  return (
    <ContactFields
      idPrefix={person?.id ?? siteId ?? 'client'}
      person={person}
      submitLabel={person ? 'Save' : 'Add contact'}
      isPending={save.isPending}
      error={save.error ? errorMessage(save.error) : null}
      onCancel={onDone}
      onSubmit={(values) =>
        save.mutate({ ...(person ? { id: person.id } : {}), clientId, siteId, ...values })
      }
    />
  )
}

export interface ContactValues {
  firstName: string
  lastName: string
  position: string
  mobile: string
  phone: string
  email: string
}

/**
 * The fields of a person, for the client record and the job card alike.
 *
 * One form, so a person typed in from a job is the same record, with the same
 * fields, as one typed in on the client. `children` sits above the buttons,
 * for the one question only the job card asks.
 */
export function ContactFields({
  idPrefix,
  person,
  submitLabel,
  isPending,
  error,
  onSubmit,
  onCancel,
  children,
}: {
  idPrefix: string
  person: ClientPerson | null
  submitLabel: string
  isPending: boolean
  error: string | null
  onSubmit: (values: ContactValues) => void
  onCancel: () => void
  children?: ReactNode
}) {
  const [firstName, setFirstName] = useState(person?.firstName ?? '')
  const [lastName, setLastName] = useState(person?.lastName ?? '')
  const [position, setPosition] = useState(
    person && !positionIsRoleWord(person.position) ? (person.position ?? '') : '',
  )
  const [mobile, setMobile] = useState(person?.mobile ?? '')
  const [phone, setPhone] = useState(person?.phone ?? '')
  const [email, setEmail] = useState(person?.email ?? '')

  const ready = firstName.trim().length > 0

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (!ready || isPending) return
        onSubmit({ firstName: firstName.trim(), lastName, position, mobile, phone, email })
      }}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="First name" htmlFor={`contact-first-${idPrefix}`} required>
          <Input
            id={`contact-first-${idPrefix}`}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Last name" htmlFor={`contact-last-${idPrefix}`}>
          <Input
            id={`contact-last-${idPrefix}`}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </Field>
        <Field label="Role" htmlFor={`contact-role-${idPrefix}`}>
          <Input
            id={`contact-role-${idPrefix}`}
            value={position}
            placeholder="Accounts, tenant…"
            onChange={(event) => setPosition(event.target.value)}
          />
        </Field>
        <Field label="Mobile" htmlFor={`contact-mobile-${idPrefix}`}>
          <Input
            id={`contact-mobile-${idPrefix}`}
            value={mobile}
            onChange={(event) => setMobile(event.target.value)}
          />
        </Field>
        <Field label="Phone" htmlFor={`contact-phone-${idPrefix}`}>
          <Input
            id={`contact-phone-${idPrefix}`}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </Field>
        <Field label="Email" htmlFor={`contact-email-${idPrefix}`}>
          <Input
            id={`contact-email-${idPrefix}`}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
      </div>
      {children}
      {error ? (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex gap-1.5">
        <Button type="submit" size="sm" variant="primary" loading={isPending} disabled={!ready}>
          {submitLabel}
        </Button>
        <Button type="button" size="sm" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function TextButton({
  children,
  onClick,
  disabled,
  tone = 'default',
}: {
  children: string
  onClick: () => void
  disabled?: boolean
  tone?: 'default' | 'danger'
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        tone === 'danger'
          ? 'text-xs font-semibold text-ink-500 hover:text-red-700 disabled:opacity-50'
          : 'text-xs font-semibold text-brand-600 hover:text-brand-700 disabled:opacity-50'
      }
    >
      {children}
    </button>
  )
}

function personName(person: ClientPerson): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ')
}

function personLine(person: ClientPerson): string {
  const position = positionIsRoleWord(person.position) ? null : person.position
  const phone = person.mobile ?? person.phone
  return [position, phone, person.email].filter(Boolean).join(' · ') || 'No number or email'
}
